
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { HelpCircle, BookOpen, Globe, FileText, Building, Shield, CreditCard, Users, MessageSquare } from "lucide-react";
import { useTranslation } from "react-i18next";
import PageLayout from "@/components/layout/PageLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const FAQ = () => {
  const navigate = useNavigate();
  const { t } = useTranslation("faq");

  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const faqCategories = [
    {
      id: "general",
      icon: <HelpCircle className="h-5 w-5" />,
      color: "text-blue-600"
    },
    {
      id: "platform",
      icon: <Globe className="h-5 w-5" />,
      color: "text-green-600"
    },
    {
      id: "tender",
      icon: <FileText className="h-5 w-5" />,
      color: "text-purple-600"
    },
    {
      id: "business",
      icon: <Building className="h-5 w-5" />,
      color: "text-orange-600"
    },
    {
      id: "legal",
      icon: <Shield className="h-5 w-5" />,
      color: "text-red-600"
    },
    {
      id: "account",
      icon: <Users className="h-5 w-5" />,
      color: "text-indigo-600"
    }
  ];

  return (
    <PageLayout
      title={t("title")}
      description={t("subtitle")}
      icon={<BookOpen className="h-6 w-6" />}
    >
      <div className="container max-w-5xl mx-auto py-12 px-4">
        <motion.div 
          className="space-y-8"
          initial="hidden"
          animate="visible"
          variants={fadeIn}
        >
          {/* Search Bar (visual only for now) */}
          <div className="relative max-w-2xl mx-auto mb-12">
            <div className="flex items-center border-2 border-gray-200 rounded-lg overflow-hidden focus-within:border-primary">
              <input
                type="text"
                placeholder={t("searchPlaceholder")}
                className="flex-grow px-4 py-3 outline-none"
              />
              <Button variant="ghost" className="px-4">
                <MessageSquare className="h-5 w-5" />
              </Button>
            </div>
            <p className="text-sm text-gray-500 mt-2 text-center">{t("popularSearches")}</p>
          </div>
          
          {/* FAQ Tabs - Modified to prevent horizontal scrolling */}
          <Tabs defaultValue="general" className="w-full">
            <div className="mb-8 border rounded-lg bg-white">
              <TabsList className="w-full flex flex-wrap justify-center p-1">
                {faqCategories.map((category) => (
                  <TabsTrigger
                    key={category.id}
                    value={category.id}
                    className="flex items-center gap-2 px-4 py-2 m-1"
                  >
                    <span className={category.color}>{category.icon}</span>
                    <span className="hidden sm:inline">{t(`categories.${category.id}.title`)}</span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>
            
            {faqCategories.map((category) => (
              <TabsContent key={category.id} value={category.id} className="space-y-6">
                <div>
                  <h2 className="text-2xl font-bold mb-6">
                    {t(`categories.${category.id}.title`)}
                  </h2>
                  <p className="text-gray-600 mb-8">
                    {t(`categories.${category.id}.description`)}
                  </p>
                  
                  <Accordion type="single" collapsible className="space-y-4">
                    {/* Fix: Properly handle the questionsCount by ensuring it's a number */}
                    {(() => {
                      // Get questionsCount and ensure it's a number
                      const count = t(`categories.${category.id}.questionsCount`, { returnObjects: true });
                      const questionsCount = typeof count === 'number' ? count : 0;
                      
                      // Create array of specified length for mapping
                      return Array.from({ length: questionsCount }).map((_, idx) => {
                        const questionKey = `q${idx + 1}`;
                        return (
                          <AccordionItem 
                            key={`${category.id}-${questionKey}`}
                            value={questionKey}
                            className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
                          >
                            <AccordionTrigger className="px-6 py-4 hover:no-underline hover:bg-gray-50">
                              <div className="flex items-start gap-3 text-left">
                                <span className={`${category.color} flex-shrink-0 mt-1`}>
                                  {category.icon}
                                </span>
                                <span>
                                  {t(`categories.${category.id}.questions.${questionKey}.question`)}
                                </span>
                              </div>
                            </AccordionTrigger>
                            <AccordionContent className="px-6 py-4 text-gray-600 ml-9">
                              {t(`categories.${category.id}.questions.${questionKey}.answer`)}
                            </AccordionContent>
                          </AccordionItem>
                        );
                      });
                    })()}
                  </Accordion>
                </div>
              </TabsContent>
            ))}
          </Tabs>

          {/* Still Have Questions Section */}
          <motion.div 
            className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-xl mt-16 text-center"
            whileHover={{ scale: 1.01 }}
            transition={{ type: "spring", stiffness: 300 }}
          >
            <h3 className="text-2xl font-semibold mb-3">
              {t("moreQuestions.title")}
            </h3>
            <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
              {t("moreQuestions.description")}
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button 
                onClick={() => navigate("/support/technical")} 
                size="lg"
                className="flex items-center gap-2"
              >
                <MessageSquare className="h-4 w-4" />
                {t("moreQuestions.supportButton")}
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate("/request-demo")}
                size="lg"
                className="flex items-center gap-2"
              >
                <CreditCard className="h-4 w-4" />
                {t("moreQuestions.demoButton")}
              </Button>
            </div>
          </motion.div>
          
          {/* Related Resources */}
          <div className="mt-16">
            <h3 className="text-xl font-semibold mb-6 text-center">{t("relatedResources.title")}</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {["platform", "legal", "pricing"].map((resource) => (
                <motion.div 
                  key={resource}
                  className="bg-white border border-gray-200 rounded-lg p-6 text-center hover:shadow-md transition-shadow"
                  whileHover={{ y: -5 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <h4 className="font-medium mb-2">{t(`relatedResources.${resource}.title`)}</h4>
                  <p className="text-sm text-gray-500 mb-4">{t(`relatedResources.${resource}.description`)}</p>
                  <Button 
                    variant="link" 
                    onClick={() => navigate(t(`relatedResources.${resource}.link`))}
                  >
                    {t(`relatedResources.${resource}.linkText`)}
                  </Button>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </PageLayout>
  );
};

export default FAQ;
